<script setup lang="ts">
import { computed, onMounted } from 'vue'
import type { StoredMessage } from '../storage/db'
import { displayName } from '../lib/format'
import { isLive, mediaKindOf, mediaTitle, partyStarts } from '../lib/party'
import { useParty } from '../stores/party'
import { usePlayer } from '../stores/player'
import { useSession } from '../stores/session'

const props = defineProps<{ msg: StoredMessage }>()
const session = useSession()
const party = useParty()
const player = usePlayer()
const p = computed(() => (props.msg.envelope.type === 'party' ? props.msg.envelope.party : null))
const running = computed(() => !!p.value && partyStarts(session.messages[props.msg.conv] ?? []).get(p.value.id) === true)
// Joinable only while someone's in it; a room everyone left without ending is over.
const live = computed(
  () => running.value && !!p.value && isLive(session.messages[props.msg.conv] ?? [], p.value.id, session.inParty[props.msg.conv] ?? []),
)
onMounted(() => void session.loadPartyParticipants(props.msg.conv))
const joinedHere = computed(() => party.joined?.id === p.value?.id)
const kind = computed(() => mediaKindOf(p.value?.queue[0]))
const what = computed(() => (kind.value === 'video' ? 'watch party' : 'listening room'))

function join(): void {
  if (!p.value) return
  player.stop()
  party.join(props.msg.conv, p.value.id)
}
</script>

<template>
  <!-- Only while it's joinable; once it's over it leaves nothing behind. -->
  <div v-if="p && (live || joinedHere)" class="mt-3 flex max-w-md items-center gap-3 border border-border-strong px-3 py-2.5" data-testid="party-card">
    <span class="mono flex h-9 w-9 shrink-0 items-center justify-center border border-border text-text-muted" aria-hidden="true">{{ kind === 'video' ? '▶▶' : '∿' }}</span>
    <div class="min-w-0 flex-1">
      <div class="truncate text-sm">{{ displayName(session.profiles, msg.sender.user_id) }} started a {{ what }}</div>
      <div class="mono truncate text-text-muted">
        {{ mediaTitle(p.queue[0]) }}<template v-if="p.queue.length > 1"> + {{ p.queue.length - 1 }} more</template>
      </div>
    </div>
    <span v-if="joinedHere" class="mono text-text-primary uppercase">Joined</span>
    <button v-else class="btn-primary h-7" data-testid="party-join" @click="join">Join</button>
  </div>
</template>
